import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import MathText from "@/components/MathText";
import { ArrowLeft, CheckCircle2, XCircle, Save, Loader2, AlertCircle, Sparkles } from "lucide-react";

export default function TeacherAttemptDetail() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [grades, setGrades] = useState({}); // question_id -> { points_awarded, feedback }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [aiBusy, setAiBusy] = useState({}); // question_id -> bool
  const [aiInfo, setAiInfo] = useState({}); // question_id -> {suggested_points, feedback}

  const load = () =>
    api.get(`/teacher/attempts/${attemptId}`).then((r) => {
      setAttempt(r.data);
      // Seed grades map with current values for descriptive
      const seed = {};
      for (const a of r.data.answers || []) {
        if (a.type === "descriptive") {
          seed[a.question_id] = {
            points_awarded: a.points_awarded || 0,
            feedback: a.feedback || "",
          };
        }
      }
      setGrades(seed);
    });

  useEffect(() => {
    load();
  }, [attemptId]);

  const setGrade = (qid, patch) =>
    setGrades((g) => ({ ...g, [qid]: { ...(g[qid] || {}), ...patch } }));

  const requestAiGrade = async (qid) => {
    setAiBusy((s) => ({ ...s, [qid]: true }));
    setErr("");
    try {
      const { data } = await api.post(`/teacher/attempts/${attemptId}/ai-grade`, { question_id: qid });
      setAiInfo((s) => ({ ...s, [qid]: data }));
      setGrade(qid, { points_awarded: data.suggested_points, feedback: data.feedback });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setAiBusy((s) => ({ ...s, [qid]: false }));
    }
  };

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      const payload = {
        grades: Object.entries(grades).map(([question_id, v]) => ({
          question_id,
          points_awarded: Number(v.points_awarded) || 0,
          feedback: v.feedback || "",
        })),
      };
      await api.post(`/teacher/attempts/${attemptId}/grade`, payload);
      setSavedAt(new Date());
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!attempt) {
    return (
      <AppShell title="Attempt review">
        <p className="text-slate-500" data-testid="teacher-attempt-loading">Loading…</p>
      </AppShell>
    );
  }

  const descriptiveCount = (attempt.answers || []).filter((a) => a.type === "descriptive").length;
  const pendingCount = (attempt.answers || []).filter(
    (a) => a.type === "descriptive" && a.pending_grading
  ).length;

  return (
    <AppShell title="Attempt review" subtitle="Grade descriptive answers and share feedback.">
      <button onClick={() => nav(-1)} className="btn-ghost text-sm mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-3 gap-6" data-testid="teacher-attempt-detail">
        {/* Summary */}
        <div className="lg:col-span-1 space-y-4">
          <div className="qs-card p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">Student</p>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-1" data-testid="attempt-student-name">
              {attempt.student_name}
            </h2>
            <p className="text-sm text-slate-500 mt-1">{attempt.exam_title}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Score</p>
                <p className="font-display text-2xl font-black text-slate-900 mt-1" data-testid="attempt-score-percent">
                  {attempt.score_percent}%
                </p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Points</p>
                <p className="font-display text-2xl font-black text-slate-900 mt-1">
                  {attempt.score}<span className="text-sm text-slate-500">/{attempt.total_points}</span>
                </p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 col-span-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Result</p>
                <p className="font-semibold text-slate-800 mt-1">
                  {attempt.passed ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Passed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-600"><XCircle className="w-4 h-4" /> Failed</span>
                  )}
                  <span className="text-xs text-slate-500 ml-2">(pass mark {attempt.exam_passing_score}%)</span>
                </p>
              </div>
            </div>
          </div>

          <div className="qs-card p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Grading progress</p>
            <p className="mt-2 text-slate-800">
              {descriptiveCount === 0 ? (
                "No descriptive questions to grade."
              ) : pendingCount > 0 ? (
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                  <AlertCircle className="w-4 h-4" /> {pendingCount}/{descriptiveCount} pending
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> All graded
                </span>
              )}
            </p>
            {descriptiveCount > 0 && (
              <button
                onClick={submit}
                disabled={busy}
                className="btn-primary w-full mt-4 justify-center disabled:opacity-70"
                data-testid="save-grades-button"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {busy ? "Saving…" : "Save grades"}
              </button>
            )}
            {savedAt && !busy && (
              <p className="mt-3 text-xs text-emerald-700" data-testid="saved-indicator">
                Saved at {savedAt.toLocaleTimeString()}
              </p>
            )}
            {err && <p className="mt-3 text-sm text-rose-600" data-testid="grade-error">{err}</p>}
          </div>
        </div>

        {/* Answers */}
        <div className="lg:col-span-2 space-y-3">
          {(attempt.answers || []).map((a, i) => {
            const isMCQ = a.type === "mcq";
            const g = grades[a.question_id];
            return (
              <div key={a.question_id} className="qs-card p-6" data-testid={`teacher-answer-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">
                    Q{i + 1} · {isMCQ ? "Multiple choice" : "Descriptive"}
                  </p>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    max {a.max_points} pt
                  </span>
                </div>
                <p className="mt-3 text-slate-800 font-semibold"><MathText>{a.question_text}</MathText></p>

                {isMCQ ? (
                  <div className="mt-4 space-y-2">
                    {(a.question_options || []).map((opt, oi) => {
                      const chosen = a.selected_index === oi;
                      const correct = a.correct_index === oi;
                      return (
                        <div
                          key={`${a.question_id || a.id}-opt-${oi}`}
                          className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${
                            correct
                              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                              : chosen
                                ? "bg-rose-50 border-rose-200 text-rose-800"
                                : "bg-slate-50 border-slate-100 text-slate-700"
                          }`}
                        >
                          <span className="text-xs font-bold w-5">{String.fromCharCode(65 + oi)}</span>
                          <span className="flex-1"><MathText>{opt}</MathText></span>
                          {chosen && <span className="text-[10px] font-bold uppercase">Selected</span>}
                          {correct && <span className="text-[10px] font-bold uppercase">Correct</span>}
                        </div>
                      );
                    })}
                    <p className="text-xs text-slate-500 mt-2">
                      Auto-graded: {a.correct ? "+" : ""}{a.points_awarded} / {a.max_points}
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Student&rsquo;s answer</p>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">
                        {a.text_answer || <span className="text-slate-400 italic">— no answer submitted —</span>}
                      </p>
                    </div>
                    {a.sample_answer && (
                      <div className="p-3 rounded-lg bg-indigo-50/60 border border-indigo-100">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-600 mb-1">Sample answer (reference)</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{a.sample_answer}</p>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-[120px_1fr] gap-3 items-start">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Points</label>
                        <input
                          type="number"
                          min={0}
                          max={a.max_points}
                          value={g?.points_awarded ?? 0}
                          onChange={(e) => setGrade(a.question_id, { points_awarded: e.target.value })}
                          className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                          data-testid={`grade-points-${i}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Feedback (optional)</label>
                        <textarea
                          rows={2}
                          value={g?.feedback ?? ""}
                          onChange={(e) => setGrade(a.question_id, { feedback: e.target.value })}
                          placeholder="Nice reasoning; consider adding..."
                          className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                          data-testid={`grade-feedback-${i}`}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => requestAiGrade(a.question_id)}
                        disabled={!!aiBusy[a.question_id]}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-95 disabled:opacity-60 transition"
                        data-testid={`ai-grade-${i}`}
                      >
                        {aiBusy[a.question_id] ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        {aiBusy[a.question_id] ? "Analyzing…" : "Suggest with AI"}
                      </button>
                      {aiInfo[a.question_id] && (
                        <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-1" data-testid={`ai-suggested-${i}`}>
                          AI: {aiInfo[a.question_id].suggested_points}/{a.max_points} · {aiInfo[a.question_id].model}
                        </span>
                      )}
                    </div>
                    {!a.pending_grading && (
                      <p className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Currently graded: {a.points_awarded}/{a.max_points}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
